import { nanoid } from "nanoid";
import qs from "query-string";

import * as api from "../../apiv2/customers";
import * as alertActions from "../alerts/alerts.actions";
import { invalidateSearchCacheIfGoSearch } from "../globalSearch/globalSearch.actions";
import { handleError } from "../utils/error";

import {
  REMOVE_SELECTED_CONTACT,
  SET_CURRENT_PAGE,
  SET_CUSTOMER,
  SET_CUSTOMER_ADDRESSES,
  SET_CUSTOMER_CONTACTS,
  SET_CUSTOMER_LISTS,
  SET_IS_ARCHIVED_CUSTOMER_INCLUDED,
  SET_IS_CONTACTS_MODAL_OPEN,
  SET_IS_FETCHING_CUSTOMER_ADDRESSES,
  SET_IS_FETCHING_CUSTOMER_CONTACTS,
  SET_IS_FETCHING_CUSTOMER_LISTS,
  SET_NEXT_PAGE_OFFSET,
  SET_PAGE_LIMIT,
  SET_PAGE_OFFSET,
  SET_PREVIOUS_PAGE_OFFSET,
  SET_SEARCH,
  SET_SEARCH_CUSTOMER,
  SET_SELECTED_CONTACTS,
  SET_SORT,
  SET_TOTAL_COUNT,
  UPDATE_SELECTED_CONTACTS,
} from "./customers.actionTypes";
import * as selectors from "./customers.selectors";

export const postCustomer = (data) => async (dispatch) => {
  try {
    const response = await api.postCustomer(data)();
    dispatch(invalidateSearchCacheIfGoSearch("customers"));
    return response;
  } catch (error) {
    handleError(error, dispatch);
  }
};

// CUSTOMER CONTACTS
export const setIsFetchingCustomerContacts = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_CUSTOMER_CONTACTS,
    payload: status,
  });
};

export const setIsContactsModalOpen =
  ({ status, type, contact }) =>
  (dispatch) => {
    dispatch({
      type: SET_IS_CONTACTS_MODAL_OPEN,
      payload: { status, type, contact },
    });
  };

export const setSelectedContacts = (data) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_CONTACTS,
    payload: data,
  });
};

export const removeSelectedContact = (data) => (dispatch) => {
  dispatch({
    type: REMOVE_SELECTED_CONTACT,
    payload: data,
  });
};

export const setCustomer = (data) => (dispatch) => {
  dispatch({
    type: SET_CUSTOMER,
    payload: data,
  });
};

export const updateSelectedContacts = (data) => (dispatch) => {
  dispatch({
    type: UPDATE_SELECTED_CONTACTS,
    payload: data,
  });
};

export const setCustomerContacts = (data) => (dispatch) => {
  dispatch({
    type: SET_CUSTOMER_CONTACTS,
    payload: data,
  });
};

export const setSearch = (data) => (dispatch) => {
  dispatch({
    type: SET_SEARCH,
    payload: data,
  });
};

export const fetchCustomer = (customerUid) => async (dispatch) => {
  try {
    const response = await api.getCustomer(customerUid)();
    const customerData = response.data;
    dispatch(setCustomer(customerData));
    return customerData;
  } catch (error) {
    handleError(error, dispatch);
  }
};

export const fetchCustomerContacts =
  ({ customerUid, contactSearch = "", params = {} }) =>
  async (dispatch) => {
    dispatch(setIsFetchingCustomerContacts(true));

    try {
      const response = await api.getCustomerContacts({ customerUid, contactSearch, params })();
      dispatch(setCustomerContacts(response));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingCustomerContacts(false));
    }
  };

export const updateCustomerContacts =
  ({ uid, customerUid, data }) =>
  async (dispatch) => {
    dispatch(setIsFetchingCustomerContacts(true));

    try {
      await api.putCustomerContacts({
        uid,
        customerUid,
        data,
      })();
      dispatch(fetchCustomerContacts({ customerUid }));
      dispatch(invalidateSearchCacheIfGoSearch("customers"));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingCustomerContacts(false));
    }
  };

export const createCustomerContacts = (customerUid, data) => async (dispatch) => {
  dispatch(setIsFetchingCustomerContacts(true));

  try {
    await api.postCustomerContacts(customerUid, data)();
    dispatch(fetchCustomerContacts({ customerUid }));
    dispatch(invalidateSearchCacheIfGoSearch("customers"));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingCustomerContacts(false));
  }
};

// Pagination Customer Lists
export const setIsArchivedCustomerIncluded = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_ARCHIVED_CUSTOMER_INCLUDED,
    payload: status,
  });
};

export const setSearchCustomer = (search) => (dispatch) => {
  dispatch({
    type: SET_SEARCH_CUSTOMER,
    payload: search,
  });
};

export const setIsFetchingCustomerLists = (data) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_CUSTOMER_LISTS,
    payload: data,
  });
};

export const setCustomerLists = (data) => (dispatch) => {
  dispatch({
    type: SET_CUSTOMER_LISTS,
    payload: data,
  });
};

export const setCurrentPage = (data) => (dispatch) => {
  dispatch({
    type: SET_CURRENT_PAGE,
    payload: data,
  });
};

export const setTotalCount = (data) => (dispatch) => {
  dispatch({
    type: SET_TOTAL_COUNT,
    payload: data,
  });
};

export const setNextPageOffset = (data) => (dispatch) => {
  dispatch({
    type: SET_NEXT_PAGE_OFFSET,
    payload: data,
  });
};

export const setPreviousPageOffset = (data) => (dispatch) => {
  dispatch({
    type: SET_PREVIOUS_PAGE_OFFSET,
    payload: data,
  });
};

export const setPageOffset = (search) => (dispatch) => {
  dispatch({
    type: SET_PAGE_OFFSET,
    payload: search,
  });
};

export const setPageLimit = (search) => (dispatch) => {
  dispatch({
    type: SET_PAGE_LIMIT,
    payload: search,
  });
};

export const setSort = (sort) => (dispatch) => {
  dispatch({
    type: SET_SORT,
    payload: sort,
  });
};

export const resetPaginationAction = () => (dispatch) => {
  dispatch(setTotalCount(0));
  dispatch(setCurrentPage(0));
  dispatch(setPageOffset(0));
  dispatch(setNextPageOffset(0));
  dispatch(setPreviousPageOffset(0));
};

export const fetchCustomerLists = () => async (dispatch, getState) => {
  const state = getState();
  try {
    dispatch(setIsFetchingCustomerLists(true));
    const search = selectors.getSearchCustomer(state);
    const limit = selectors.getPagination(state).limit;
    const offset = selectors.getPagination(state).offset;
    const sort = selectors.getSort(state);
    const with_archived = selectors.isArchivedCustomerIncluded(state);
    const params = {
      search: search || undefined,
      limit,
      offset,
      sort_by: sort !== "" ? sort : undefined,
      with_archived,
    };

    const { data } = await api.getCustomerLists(params)();
    dispatch(setCustomerLists(data.results));
    dispatch(setTotalCount(data.count));
    dispatch(setCurrentPage(offset / limit));
    if (data.next) {
      const parsedNextPage = qs.parse(data.next);
      dispatch(setNextPageOffset(parsedNextPage?.offset ?? 0));
    }

    if (data.previous) {
      const parsedPreviousPage = qs.parse(data.previous);
      dispatch(setPreviousPageOffset(parsedPreviousPage?.offset ?? 0));
    }
  } catch (error) {
    console.log(error);
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingCustomerLists(false));
  }
};

export const archiveCustomer =
  (customerUid, offset = null) =>
  async (dispatch) => {
    try {
      await api.changeCustomerData({ customerUid, data: { isDeleted: true } })();
      dispatch(invalidateSearchCacheIfGoSearch("customers"));
      if (offset === null) {
        dispatch(resetPaginationAction());
      } else {
        dispatch(setPageOffset(offset));
      }
      dispatch(fetchCustomerLists());

      dispatch(
        alertActions.createAlertAction(
          nanoid(),
          "Customer archived.",
          true,
          "dark",
          "Undo",
          async () => {
            await dispatch(unArchiveCustomer(customerUid));
          }
        )
      );
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const unArchiveCustomer = (customerUid) => async (dispatch) => {
  try {
    await api.changeCustomerData({ customerUid, data: { isDeleted: false } })();
    dispatch(invalidateSearchCacheIfGoSearch("customers"));
    dispatch(resetPaginationAction());
    dispatch(fetchCustomerLists());
  } catch (error) {
    handleError(error, dispatch);
  }
};

// CUSTOMER ADDRESSES
export const setIsFetchingCustomerAddresses = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_CUSTOMER_ADDRESSES,
    payload: status,
  });
};

export const setCustomerAddresses = (data) => (dispatch) => {
  dispatch({
    type: SET_CUSTOMER_ADDRESSES,
    payload: data,
  });
};

export const fetchCustomerAddresses =
  ({ customerUid, addressSearch = "", params = {} }) =>
  async (dispatch) => {
    dispatch(setIsFetchingCustomerAddresses(true));

    try {
      const response = await api.getCustomerAddresses({ customerUid, addressSearch, params })();
      dispatch(setCustomerAddresses(response));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingCustomerAddresses(false));
    }
  };

export const updateCustomerAddress =
  ({ uid, customerUid, data }) =>
  async (dispatch) => {
    dispatch(setIsFetchingCustomerAddresses(true));

    try {
      await api.putCustomerAddress({
        uid,
        customerUid,
        data,
      })();
      dispatch(fetchCustomerAddresses({ customerUid }));
    } catch (error) {
      handleError(error, dispatch);
    } finally {
      dispatch(setIsFetchingCustomerAddresses(false));
    }
  };

export const createCustomerAddress = (customerUid, data) => async (dispatch) => {
  dispatch(setIsFetchingCustomerAddresses(true));

  try {
    await api.postCustomerAddress(customerUid, data)();
    dispatch(fetchCustomerAddresses({ customerUid }));
  } catch (error) {
    handleError(error, dispatch);
  } finally {
    dispatch(setIsFetchingCustomerAddresses(false));
  }
};
